import { AlertCircle, Check } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Button as NewButton } from '@/components/ui/NewButton'
import type { FolderTree as FolderTreeType } from '@/hooks/useFolders'
import { useFolderTree } from '@/hooks/useFolders'
import useMatter from '@/hooks/useMatter'
import { useOAuthPopupFlow } from '@/hooks/useOAuthPopupFlow'
import { cn } from '@/lib/utils'
import { FolderTree } from '../folders/FolderTree'
import { IManageContainerIcon } from '../imanage/IManageContainerIcon'
import { IManageFolderPickerV2 } from '../imanage/IManageFolderPickerV2'
import type { IManageFolderPayload } from '../imanage/imanageFolderPickerUtils'
import { findNestedIManagePicks, nestedIManagePicksMessage } from '../imanage/imanageFolderPickerUtils'
import { Loading } from '../shared'
import {
  authenticateImanage,
  createImanageV2Syncs,
  fetchImanageDocumentCounts,
  type IManageV2Auth,
  type IManageV2Source,
  imanageFileCountLabel,
  imanagePreviewKind,
  lookupImanageDocumentCount,
  mapImanagePickerItems,
  resolveImanageSources,
  resolvingSourcesLabel,
  totalImanageDocumentCount,
} from './addSyncImanageV2'
import {
  isPreviewPlaceholderFolder,
  isPreviewSampleFile,
  isPreviewSourceFolder,
  previewNodesForSampleState,
  previewNodesForSamples,
  previewSourceFolder,
  sourcePreviewChildren,
} from './addSyncPreviewTree'
import {
  crimsonDestinationLabel,
  imanageParentPath,
  sortFoldersByName,
} from './addSyncShared'
import {
  attachChildrenToFolder,
  defaultSyncMappingMode,
  isMergeToRootBlocked,
  mergedPreviewSampleState,
  namesWouldDoubleNest,
  pruneTreeToSelectedFolder,
  type SyncMappingMode,
  syncMappingOverrideCopy,
} from './syncMapping'

type V2Step = 'auth' | 'destination' | 'preview' | 'resolving'

function IManageSourceMark({ name }: { name: string }) {
  return (
    <span className="inline-flex max-w-[11rem] items-center gap-1 rounded-full bg-white px-2 py-0.5 text-xs text-gray-600">
      <img src="/iManage-logo.png" alt="" className="h-3 w-3 shrink-0 object-contain" />
      <span className="truncate">{name}</span>
    </span>
  )
}

interface AddSyncV2WizardProps {
  matterGuid: string
  onCreated: (syncFolderId: number) => void
  onCancel: () => void
  interceptDismissRef: React.MutableRefObject<() => boolean>
}

export function AddSyncV2Wizard({
  matterGuid,
  onCreated,
  onCancel,
  interceptDismissRef,
}: AddSyncV2WizardProps) {
  const [step, setStep] = useState<V2Step>('auth')
  const { matter } = useMatter(matterGuid)
  const matterName = matter?.name?.trim() || 'this matter'
  const { folderTree, isLoading: loadingFolders } = useFolderTree(matterGuid)
  const { runOAuthPopup } = useOAuthPopupFlow()

  const [isAuthenticating, setIsAuthenticating] = useState(true)
  const [auth, setAuth] = useState<IManageV2Auth | null>(null)
  const [selectedFolders, setSelectedFolders] = useState<IManageV2Source[]>([])
  const [documentCounts, setDocumentCounts] = useState<Record<string, number>>({})
  const [destinationFolder, setDestinationFolder] = useState<FolderTreeType | null | undefined>(undefined)
  const [mappingMode, setMappingMode] = useState<SyncMappingMode>('merge')
  const [showIManageFolderPicker, setShowIManageFolderPicker] = useState(false)
  const [resolvingSourceCount, setResolvingSourceCount] = useState(0)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    interceptDismissRef.current = () => {
      if (showIManageFolderPicker && !isCreating) {
        setShowIManageFolderPicker(false)
        return true
      }
      return isCreating || isAuthenticating || step === 'auth'
    }
    return () => {
      interceptDismissRef.current = () => false
    }
  }, [interceptDismissRef, isAuthenticating, isCreating, showIManageFolderPicker, step])

  const handleAuthenticate = async () => {
    setIsAuthenticating(true)
    setError(null)
    try {
      const nextAuth = await authenticateImanage(runOAuthPopup)
      setAuth(nextAuth)
      setStep('destination')
      setShowIManageFolderPicker(true)
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string; message?: string } }; message?: string }
      setError(
        axiosError.response?.data?.detail
        || axiosError.response?.data?.message
        || axiosError.message
        || 'Authentication failed',
      )
    } finally {
      setIsAuthenticating(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: start OAuth when the V2 wizard mounts
  useEffect(() => {
    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        void handleAuthenticate()
      }
    }, 100)
    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [])

  const handleIManageFoldersSelected = async (folders: IManageFolderPayload[]) => {
    let mapped = mapImanagePickerItems(folders as Array<Record<string, unknown>>)
    setShowIManageFolderPicker(false)

    mapped = sortFoldersByName(mapped)
    setSelectedFolders(mapped)

    const shouldResolve = Boolean(mapped.length > 0 && auth?.providerConfig.access_token && auth.providerConfig.customer_id)
    if (shouldResolve && auth) {
      setResolvingSourceCount(mapped.length)
      setStep('resolving')
      try {
        mapped = await resolveImanageSources(auth, mapped)
      } catch (resolveError) {
        console.warn('Failed to resolve iManage folder paths:', resolveError)
      }
      mapped = sortFoldersByName(mapped)
    }

    const nested = findNestedIManagePicks(mapped)
    if (nested) {
      setError(nestedIManagePicksMessage(nested.parent.name || nested.parent.id, nested.child.name || nested.child.id))
      setSelectedFolders(mapped)
      setStep('destination')
      return
    }
    setError(null)
    setSelectedFolders(mapped)
    setStep('destination')
  }

  const foldersToSync = selectedFolders
  const selectedSourceIds = foldersToSync.map((item) => item.id).filter(Boolean)
  const selectedSourceIdsKey = selectedSourceIds.join('\0')
  const totalFileCount = totalImanageDocumentCount(documentCounts, selectedSourceIds)
  const destinationIsRoot = destinationFolder === null
  const destinationLabel = crimsonDestinationLabel(destinationFolder, folderTree)
  const mappingOverrideCopy = syncMappingOverrideCopy({
    destinationIsRoot,
    sourceCount: foldersToSync.length,
    sourceName: foldersToSync[0]?.name,
    destinationName: destinationFolder?.name,
  })
  const mergeToRootBlocked = isMergeToRootBlocked(mappingMode, destinationIsRoot)
  const previewSources = sortFoldersByName(foldersToSync).map((item) => ({
    name: item.name,
    kind: imanagePreviewKind(item),
  }))
  const previewTree = useMemo(() => {
    const sortedSources = sortFoldersByName(foldersToSync)
    const extras = mappingMode === 'nest'
      ? sortedSources.map((item, index) => previewSourceFolder(
        item.name,
        index,
        previewNodesForSamples(sourcePreviewChildren(item), index),
      ))
      : previewNodesForSampleState(
        mergedPreviewSampleState(sortedSources.map(sourcePreviewChildren)),
        0,
      )
    if (destinationIsRoot || !destinationFolder) {
      return extras
    }
    const pruned = pruneTreeToSelectedFolder(folderTree, destinationFolder.folder_id)
    if (!pruned) {
      return extras
    }
    return attachChildrenToFolder(pruned, destinationFolder.folder_id, extras)
  }, [destinationFolder, destinationIsRoot, folderTree, foldersToSync, mappingMode])
  const wouldMixTrees = mappingMode === 'merge' && previewSources.length > 1
  const doubleNestSources = previewSources.filter((source) => (
    namesWouldDoubleNest(source.name, destinationFolder?.name, mappingMode, destinationIsRoot)
  ))
  const nestedIManagePick = findNestedIManagePicks(selectedFolders)
  const destinationKey = destinationFolder === undefined
    ? 'unset'
    : destinationFolder === null
      ? 'root'
      : String(destinationFolder.folder_id)
  const isBrowseStep = step === 'destination' || step === 'preview' || step === 'resolving'

  useEffect(() => {
    if (destinationKey === 'unset') {
      return
    }
    setMappingMode(defaultSyncMappingMode({
      destinationIsRoot: destinationKey === 'root',
      sourceCount: foldersToSync.length,
    }))
  }, [destinationKey, foldersToSync.length])

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch only when source ids change
  useEffect(() => {
    if (!auth?.providerConfig.access_token || selectedSourceIds.length === 0) {
      setDocumentCounts({})
      return
    }
    let cancelled = false
    setDocumentCounts({})
    const controller = new AbortController()
    void fetchImanageDocumentCounts(auth, selectedSourceIds, controller.signal).then((counts) => {
      if (!cancelled) {
        setDocumentCounts(counts)
      }
    })
    return () => {
      cancelled = true
      controller.abort()
    }
  }, [auth, selectedSourceIdsKey])

  const validateCreateSelection = (): boolean => {
    if (foldersToSync.length === 0) {
      setError('Please select at least one source folder')
      return false
    }
    if (destinationFolder === undefined) {
      setError('Please select a destination folder')
      return false
    }
    return true
  }

  const handlePreview = () => {
    if (!validateCreateSelection()) {
      return
    }
    setError(null)
    setStep('preview')
  }

  const handleCreateSync = async () => {
    if (!validateCreateSelection() || !auth) {
      return
    }
    const nested = findNestedIManagePicks(foldersToSync)
    if (nested) {
      setError(nestedIManagePicksMessage(nested.parent.name || nested.parent.id, nested.child.name || nested.child.id))
      return
    }
    if (mergeToRootBlocked) {
      setError('Contents cannot merge into Root. Keep the iManage folder name, or pick a Crimson folder.')
      return
    }

    setIsCreating(true)
    setError(null)
    try {
      const createdIds = await createImanageV2Syncs({
        matterGuid,
        destinationFolderId: destinationFolder?.folder_id || null,
        mappingMode,
        auth,
        sources: foldersToSync,
      })
      if (createdIds.length > 0) {
        onCreated(createdIds[0])
      } else {
        onCancel()
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } }; message?: string }
      setError(axiosError.response?.data?.detail || axiosError.message || 'Failed to create sync')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <>
      <DialogContent
        className={cn(
          'flex flex-col w-[80vw] max-w-5xl',
          isBrowseStep
            ? 'h-[80vh] max-h-[85vh] overflow-hidden'
            : 'max-h-[80vh]',
        )}
        hideCloseButton={isCreating}
        onInteractOutside={(event) => {
          if (showIManageFolderPicker || isCreating) {
            event.preventDefault()
          }
        }}
        onPointerDownOutside={(event) => {
          if (showIManageFolderPicker || isCreating) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {step === 'preview' ? 'Preview folder sync' : 'Add External Folder Sync'}
          </DialogTitle>
          <DialogDescription>
            {step === 'preview'
              ? `This is where files will land in ${destinationLabel}.`
              : `Sync documents from an external folder to ${matterName}`}
          </DialogDescription>
        </DialogHeader>

        <div className={cn('flex-1', isBrowseStep ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto')}>
          {step !== 'preview' && step !== 'resolving' && (
            <div
              className={cn(
                'mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md',
                selectedFolders.length > 0 ? 'min-h-0 max-h-48 overflow-y-auto' : 'shrink-0',
              )}
            >
              <div className="flex items-start gap-3 text-sm">
                {selectedFolders.length > 0 ? (
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    {selectedFolders.map((item) => {
                      const fileCount = lookupImanageDocumentCount(documentCounts, item.id)
                      return (
                        <div key={item.id} className="flex min-w-0 items-start gap-2">
                          <IManageContainerIcon
                            wstype={item.wstype === 'workspace' ? 'workspace' : 'folder'}
                            className="mt-0.5"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <div className="min-w-0 flex-1 font-medium truncate">{item.name}</div>
                              {fileCount !== undefined && (
                                <div className="shrink-0 text-xs tabular-nums text-gray-500">
                                  {imanageFileCountLabel(fileCount)}
                                </div>
                              )}
                            </div>
                            {item.wstype !== 'workspace' && (() => {
                              const parentPath = imanageParentPath(item.path)
                              return parentPath ? (
                                <div className="text-xs text-gray-500 truncate">{parentPath}</div>
                              ) : null
                            })()}
                          </div>
                        </div>
                      )
                    })}
                    {selectedFolders.length > 1 && totalFileCount !== undefined && (
                      <div className="mt-0.5 border-t border-gray-200 pt-1.5 text-xs tabular-nums text-gray-500">
                        {imanageFileCountLabel(totalFileCount)} in total
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="font-medium">iManage</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 shrink-0 text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'auth' && (
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="mb-4">
                  <div className="text-lg font-semibold">Connect to iManage</div>
                  <div className="text-sm text-gray-600 mt-1">
                    You'll be redirected to authenticate with iManage
                  </div>
                </div>
                <NewButton
                  onClick={() => void handleAuthenticate()}
                  loading={isAuthenticating}
                  loadingText="Connecting"
                  text="Connect to iManage"
                  size="lg"
                />
              </div>
            </div>
          )}

          {step === 'resolving' && (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
              <Loading size="lg" text={resolvingSourcesLabel(resolvingSourceCount)} />
            </div>
          )}

          {step === 'destination' && (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0">
                <Label>Select destination folder in {matterName}</Label>
                <p className="mt-2 text-sm text-gray-500">
                  Choose where synced documents will be stored in {matterName}
                </p>
              </div>
              {loadingFolders ? (
                <div className="flex h-32 items-center justify-center">
                  <Loading text="Loading folders" />
                </div>
              ) : (
                <div className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-md border">
                  <FolderTree
                    folders={folderTree}
                    totalDocuments={0}
                    matterSlug={matterGuid}
                    onRefresh={() => {}}
                    navigationMode="callback"
                    onFolderSelect={(folder) => setDestinationFolder(folder)}
                    selectedFolderGuid={destinationFolder === undefined ? undefined : (destinationFolder?.guid ?? null)}
                    showFolderActions={false}
                    allFilesLabel="Root"
                    allFilesInFolderList
                    allowExpandWhenDisabled
                  />
                </div>
              )}
            </div>
          )}

          {step === 'preview' && (
            <div className="flex min-h-0 flex-1 flex-col space-y-3 overflow-hidden">
              {mappingOverrideCopy ? (
                <div className="space-y-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setMappingMode('nest')}
                    className={`w-full flex items-center gap-3 rounded-md px-4 py-3 text-left transition-all border ${
                      mappingMode === 'nest'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      mappingMode === 'nest' ? 'border-gray-900 bg-gray-900' : 'border-gray-300'
                    }`}>
                      {mappingMode === 'nest' && (
                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{mappingOverrideCopy.nestLabel}</div>
                      <p className="text-xs text-gray-600 mt-0.5">{mappingOverrideCopy.nestHint}</p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMappingMode('merge')}
                    className={`w-full flex items-center gap-3 rounded-md px-4 py-3 text-left transition-all border ${
                      mappingMode === 'merge'
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className={`shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      mappingMode === 'merge' ? 'border-gray-900 bg-gray-900' : 'border-gray-300'
                    }`}>
                      {mappingMode === 'merge' && (
                        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">{mappingOverrideCopy.mergeLabel}</div>
                      <p className="text-xs text-gray-600 mt-0.5">{mappingOverrideCopy.mergeHint}</p>
                    </div>
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-600 shrink-0">
                  {foldersToSync.length === 1
                    ? 'The selected folder will be created at Root. Files cannot live at Root.'
                    : 'The selected folders will be created at Root. Files cannot live at Root.'}
                </p>
              )}

              {wouldMixTrees && (
                <p className="text-sm text-amber-800 shrink-0">
                  This merges every selected folder into one destination. Separation between those iManage folders will be lost.
                </p>
              )}
              {doubleNestSources.length > 0 && (
                <p className="text-sm text-amber-800 shrink-0">
                  This will nest inside the folder you already chose.
                </p>
              )}
              {mergeToRootBlocked && (
                <p className="text-sm text-red-600 shrink-0">
                  Contents cannot merge into Root. Keep the iManage folder name, or pick a Crimson folder.
                </p>
              )}

              <div className="min-h-0 flex-1 overflow-y-auto rounded-md border">
                <FolderTree
                  key={`${destinationKey}-${mappingMode}`}
                  folders={previewTree}
                  totalDocuments={0}
                  matterSlug={matterGuid}
                  onRefresh={() => {}}
                  navigationMode="callback"
                  onFolderSelect={() => {}}
                  showFolderActions={false}
                  allFilesLabel="Root"
                  allFilesInFolderList
                  defaultExpandAll
                  showDocumentCounts={false}
                  selectionEnabled={false}
                  isPlaceholderFolder={isPreviewPlaceholderFolder}
                  isPreviewFile={isPreviewSampleFile}
                  renderFolderAccessory={(folder) => {
                    if (isPreviewSourceFolder(folder)) {
                      return <IManageSourceMark name={folder.name} />
                    }
                    if (
                      mappingMode === 'merge'
                      && destinationFolder
                      && folder.guid === destinationFolder.guid
                    ) {
                      return previewSources.length > 1
                        ? <IManageSourceMark name="all folders" />
                        : previewSources[0]
                          ? <IManageSourceMark name={previewSources[0].name} />
                          : null
                    }
                    return null
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex justify-between w-full">
            <div>
              {step === 'preview' ? (
                <NewButton
                  variant="outline"
                  onClick={() => setStep('destination')}
                  disabled={isCreating}
                  text="Back"
                />
              ) : step === 'destination' ? (
                <NewButton
                  variant="outline"
                  onClick={() => {
                    if (!auth?.accessToken) {
                      void handleAuthenticate()
                      return
                    }
                    setShowIManageFolderPicker(true)
                  }}
                  text="Change iManage selection"
                />
              ) : null}
            </div>
            <div className="flex gap-2">
              <NewButton variant="outline" onClick={onCancel} disabled={isCreating} text="Cancel" />
              {step === 'destination' && (
                <NewButton
                  onClick={handlePreview}
                  disabled={destinationFolder === undefined || isCreating || Boolean(nestedIManagePick)}
                  loading={isCreating}
                  loadingText="Creating Sync"
                  text="Preview Sync"
                />
              )}
              {step === 'preview' && (
                <NewButton
                  onClick={() => void handleCreateSync()}
                  disabled={isCreating || mergeToRootBlocked}
                  loading={isCreating}
                  loadingText="Creating Sync"
                  text="Create Sync"
                />
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {auth?.providerConfig.access_token && (
        <IManageFolderPickerV2
          isOpen={showIManageFolderPicker}
          onClose={() => setShowIManageFolderPicker(false)}
          onFoldersSelected={handleIManageFoldersSelected}
          authData={{
            access_token: auth.providerConfig.access_token,
            customer_id: auth.providerConfig.customer_id,
            user_id: auth.providerConfig.user_id,
            dialog_token: auth.providerConfig.dialog_token,
          }}
        />
      )}
    </>
  )
}
